import { useCreateDraftWithdrawal } from './useCreateDraftWithdrawal'
import { useCallback, useMemo, useState } from 'react'
import { useSubmitWithdrawal } from './useSubmitWithdrawal'
import { useUpdateDraftWithdrawal } from './useUpdateDraftWithdrawal'
import {
  withdrawalSteps,
  withdrawalDraftDefaultData,
  withdrawFormFields,
  cryptoPaymentErrorMessages,
  cryptoPaymentErrorCodes
} from '../../cryptoPaymentConstants'
import { config } from '../../../Init/Init'
import l10n from '../../../i18n/I18N'

export const useWithdrawalFormAPI = ({ loading, setLoading, isXRPCryptoType, withdrawalFormRef }) => {
  const [withdrawalStep, setWithdrawalStep] = useState(withdrawalSteps.create)
  const [withdrawalDraftData, setWithdrawalDraftData] = useState(withdrawalDraftDefaultData)
  const [withdrawalFormError, setWithdrawalFormError] = useState('')

  const handleDraftWithdrawalError = useCallback(
    errorData => {
      // set withdrawal to the first step
      setWithdrawalStep(withdrawalSteps.create)

      // handle general errors on the redeem tab
      if (errorData instanceof Error) {
        if (config.enableSweepstakes) {
          setWithdrawalFormError(l10n.t['cryptopayment.redeem.error'])
          console.error('[CryptoPaymentModal] Failed sending redemption request!', errorData)
        } else {
          setWithdrawalFormError(l10n.t['cryptopayment.withdraw.error'])
          console.error('[CryptoPaymentModal] Failed sending withdraw request!', errorData)
        }

        return
      }

      // handle specific errors on redeem tab
      if (typeof errorData === 'number') {
        if (errorData === cryptoPaymentErrorCodes.invalidAddressError) {
          withdrawalFormRef.current.setFieldError(
            withdrawFormFields.address,
            l10n.t[cryptoPaymentErrorMessages[cryptoPaymentErrorCodes.invalidAddressError]]
          )

          return
        }

        // handle other specific errors on redeem tab
        return setWithdrawalFormError(l10n.t[cryptoPaymentErrorMessages[errorData]])
      }

      // fallback to general errors on redeem tab
      setWithdrawalFormError(
        config.enableSweepstakes ? l10n.t['cryptopayment.redeem.error'] : l10n.t['cryptopayment.withdraw.error']
      )
    },
    [withdrawalFormRef]
  )

  const { onWithdrawalDraftCreate } = useCreateDraftWithdrawal({
    loading,
    setLoading,
    isXRPCryptoType,
    setWithdrawalStep,
    setWithdrawalFormError,
    setWithdrawalDraftData,
    handleDraftWithdrawalError
  })

  const { onWithdrawalFormSubmit, withdrawalResponseData, resetWithdrawalResponseData } = useSubmitWithdrawal({
    loading,
    setLoading,
    setWithdrawalFormError,
    uuid: withdrawalDraftData.uuid,
    handleDraftWithdrawalError
  })

  const { updateDraftWithdrawal } = useUpdateDraftWithdrawal({
    loading,
    setLoading,
    isXRPCryptoType,
    setWithdrawalFormError,
    setWithdrawalDraftData,
    uuid: withdrawalDraftData.uuid,
    handleDraftWithdrawalError
  })

  const handleSubmit = useMemo(() => {
    return {
      [withdrawalSteps.create]: onWithdrawalDraftCreate,
      [withdrawalSteps.submit]: onWithdrawalFormSubmit
    }
  }, [onWithdrawalDraftCreate, onWithdrawalFormSubmit])

  return {
    withdrawalStep,
    withdrawalFormRef,
    setWithdrawalStep,
    withdrawalDraftData,
    withdrawalFormError,
    updateDraftWithdrawal,
    withdrawalResponseData,
    setWithdrawalDraftData,
    setWithdrawalFormError,
    resetWithdrawalResponseData,
    handleSubmit: handleSubmit[withdrawalStep]
  }
}
